/* The snippet that pulled this file in. Read here and nowhere else: document.currentScript is only
 * the loader's own tag while the file's top level runs, and every value below is derived from it.
 *
 * Each read is guarded rather than followed by an early return, because this block is a module of
 * its own and a module has no top level to return from. The bootstrap at the very bottom asks
 * snippetInstalled and stops there instead — same two conditions, same outcome, one place. */
export const script = document.currentScript;
export const key = script ? script.getAttribute('data-key') : '';
export const origin = script && script.src ? new URL(script.src).origin : '';
const lang = script ? (script.getAttribute('data-lang') || document.documentElement.lang || '') : '';
export const label = script ? (script.getAttribute('data-title') || 'Chat') : 'Chat';

/* Who the embedding page says is reading it. The page declares it because it is the only party that
 * knows — every request this loader makes is anonymous and identifier-free by design, and the cookie that
 * would say so never travels with them. Only the platform widget acts on it at all; a provider's own
 * website has one audience.
 *
 * Two exports, and the split is deliberate. PAGE MESSAGES have always known exactly one value: a page
 * carries the default question and, on the platform widget, a second one written for providers. Widening
 * that to a third value would make every signed-in buyer look like a visitor whose question never came,
 * and the corner would ask the server to write one on every page load forever.
 * The TRUST corner tells all three apart: which reviews of the platform convince somebody depends on which
 * side of it they are on, and '' — an anonymous visitor — means the corner mixes both sides.
 * A wrong value buys nothing either way: both are public copy, just aimed at the other half. */
const declaredAudience = script ? script.getAttribute('data-audience') : '';
export const audience = declaredAudience === 'provider' ? 'provider' : '';
export const visitorAudience =
    declaredAudience === 'provider' || declaredAudience === 'buyer' ? declaredAudience : '';

/* Whether there is anything to do at all: a script tag with a src and a key, and no launcher on the
 * page yet. The last one is the guard against a second snippet — two of them race while the config
 * loads, and the loser must not render a second launcher. */
export const snippetInstalled = Boolean(script && script.src && key
    && !document.getElementById('leadwin-agent-button'));

/* What the page declares: data-lang on the snippet, then <html lang>, cut to the two-letter code the
 * server reads. Empty when the page says nothing — the server then falls back to the widget's own
 * default language. */
export const pageLanguage = lang ? lang.slice(0, 2).toLowerCase() : '';

/** '?a=1&b=2' for the parts that have something to say, or '' for none. */
export function query(parts) {
    return parts.length ? '?' + parts.join('&') : '';
}

/* Whether the page running this loader is one of OURS standing in for the provider's website: the
 * simulated visit in the live tab. It travels to the config endpoint and the frame so the installation
 * detection can skip it — the stage cannot be told apart by its origin, because for an agent whose website is a host we
 * operate the stage and the real site share one. Declared by the page, never by a customer's snippet;
 * pasting it on a real website would only suppress that site's own detection. */
const stage = script ? script.getAttribute('data-leadwin-stage') : '';
export const isStage = stage === '1' || stage === 'true';

/**
 * The provider's page a stage page of ours stands in for, or null. A demo page shows the provider his own
 * agent on a copy of his website that lives on our host, so the address bar names OUR page; the page the
 * visitor is meant to be on is his, and that is the one whose question the bubble has to ask and whose
 * rules decide where a surface stands. The page declares it with data-leadwin-page, an absolute http(s)
 * address. Read only on a stage: on a real website the address bar is the truth, and a stray attribute
 * there must not move every surface onto another page.
 * Anything that is not an absolute http(s) address is dropped, and the loader then reads location as always.
 * @returns {{host: string, pathname: string, search: string}|null}
 */
function readVirtualPage() {
    const declared = isStage && script ? script.getAttribute('data-leadwin-page') : '';
    if (!declared) {
        return null;
    }

    try {
        const url = new URL(declared.trim());
        if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.host) {
            return null;
        }

        return { host: url.host.toLowerCase(), pathname: url.pathname || '/', search: url.search || '' };
    } catch {
        return null; // A relative or malformed address names no page.
    }
}

export const virtualPage = readVirtualPage();

/* Which consent variant of the snippet this page runs: 'granted' (consent for every visitor) or 'banner'
 * (the site's cookie banner decides per visitor, through LeadWin('consent', …)), '' for none. Only the two
 * known values travel. It goes to the config endpoint so the dashboard can compare what the website
 * actually runs with the provider's current setting — the one part of the snippet a setting rewrites.
 * What the loader itself does with it is analytics.js's business: only 'granted' starts it consented. */
const consentAttribute = script ? script.getAttribute('data-leadwin-consent') : '';
export const declaredConsent =
    consentAttribute === 'granted' || consentAttribute === 'banner' ? consentAttribute : '';

// The nudge texts are visitor-facing copy, so the config is fetched per language just like the frame.
// Deliberately WITHOUT the visitor's path. The payload is the same on every page of the site, but an
// HTTP cache is keyed by URL: a path in the query turns one cache entry per site into one per page, so
// every page of an agency's website would download the whole configuration again instead of
// revalidating it. The visited path is reported separately, where it costs a 204 — see
// requestPageCoverage. The consent attribute is fixed per snippet, so it costs no extra entry.
export const configUrl = origin + '/widget/' + encodeURIComponent(key) + '/config'
    + query([
        ...(pageLanguage ? ['lang=' + encodeURIComponent(pageLanguage)] : []),
        ...(isStage ? ['stage=1'] : []),
        ...(declaredConsent ? ['consent=' + declaredConsent] : [])
    ]);

// Where the visited path is reported instead; see requestPageCoverage.
export const pageSeenUrl = origin + '/widget/' + encodeURIComponent(key) + '/page';

/* Where a corner that is waiting for this page's question asks how far it is; see pollPageMessage. */
export const pageMessageUrl = origin + '/widget/' + encodeURIComponent(key) + '/page-message';
/* Where the loader says an automatic question was shown; its retention counts from there. */
export const pageMessageShownUrl = pageMessageUrl + '/shown';

/* Where the launcher asks, with the visitor's peek token, whether a team reply is waiting; see
 * createInboxSurface. */
export const inboxUrl = origin + '/widget/' + encodeURIComponent(key) + '/inbox';
